import { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import EmailAlertButton from "@/components/EmailAlertButton";
import {
  Calculator, Home, Building2, KeySquare, Map, FileCheck2, FileX2,
  Tractor, Landmark, ArrowRight, Sparkles, TrendingUp, Banknote, Loader2, X,
} from "lucide-react";

const fmtMoney = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA";

/** Build a query string for /annonces filters. */
const annoncesUrl = (params) => {
  const sp = new URLSearchParams({ category: "Immobilier", ...params });
  return `/annonces?${sp.toString()}`;
};

export default function ImmobilierPage() {
  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="immobilier-page">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-100 via-amber-50 to-white border border-orange-200 px-8 py-10 md:py-14">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-300/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-amber-300/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white text-orange-700 px-3 py-1 rounded-full border border-orange-200">
            <Sparkles size={12} /> Nouveau · Fodindin Immobilier
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">
            Financer, acheter, louer, vendre.<br className="hidden md:block" />
            <span className="text-orange-600">L&apos;immobilier en zone CFA, simplifie.</span>
          </h1>
          <p className="text-[var(--ink-soft)] mt-3 max-w-2xl text-base md:text-lg">
            Tout au meme endroit : simulez votre credit, trouvez votre bien, louez ou gerez vos terrains —
            connecte aux annonces deja publiees sur Fodindin.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <EmailAlertButton
              presetCategory="Immobilier"
              presetLabel="Nouvelles annonces immobilieres"
              buttonLabel="M'alerter des nouvelles annonces"
              testId="immobilier-alert-btn"
            />
            <EmailAlertButton
              presetCategory="Immobilier"
              presetTypeBien="maison,appartement"
              presetStatut="vente"
              presetEtatBien="inachevee"
              presetLabel="Maisons inachevees a reprendre"
              buttonLabel="Suivre les maisons inachevees"
              testId="immobilier-alert-inachevee-btn"
            />
          </div>
        </div>
      </section>

      {/* 4 TABLEAUX */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <FinanceCalculator />
        <BuyCard />
        <RentCard />
        <LandCard />
      </section>

      {/* CTA bas */}
      <section className="mt-12 text-center">
        <p className="text-sm text-[var(--ink-soft)]">
          Vous avez un bien a vendre ou louer ?{" "}
          <Link to="/deposer" className="text-orange-600 font-bold underline" data-testid="immobilier-cta-deposer">
            Deposez votre annonce immobiliere
          </Link>
        </p>
      </section>
    </main>
  );
}

/* ---------- Block 1: Mortgage simulator ---------- */
function FinanceCalculator() {
  const [price, setPrice] = useState(50000000);
  const [apport, setApport] = useState(10000000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(8.5);
  const [showResult, setShowResult] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);

  const { monthly, totalCost, maxBudget } = useMemo(() => {
    const loanAmt = Math.max(0, Number(price) - Number(apport));
    const r = Number(rate) / 100 / 12;
    const n = Math.max(1, Number(years) * 12);
    const m = r > 0 ? (loanAmt * r) / (1 - Math.pow(1 + r, -n)) : loanAmt / n;
    const total = m * n + Number(apport);
    const budget = Number(apport) + (r > 0 ? (m * (1 - Math.pow(1 + r, -n))) / r : m * n);
    return { monthly: m, totalCost: total, maxBudget: budget };
  }, [price, apport, years, rate]);

  return (
    <article className="glass-card p-7" data-testid="immobilier-tableau-finance">
      <Header icon={Calculator} label="Calculer votre capacite de financement" hue="orange" />
      <p className="text-sm text-[var(--ink-soft)] mt-1">Estimez votre credit immobilier aupres des banques ivoiriennes.</p>

      <div className="grid grid-cols-2 gap-3 mt-5">
        <NumberField label="Prix du bien (FCFA)" value={price} onChange={setPrice} testId="fin-price" />
        <NumberField label="Apport personnel (FCFA)" value={apport} onChange={setApport} testId="fin-apport" />
        <NumberField label="Duree (annees)" value={years} onChange={setYears} testId="fin-years" />
        <NumberField label="Taux interet (% annuel)" value={rate} onChange={setRate} step="0.1" testId="fin-rate" />
      </div>

      <button
        type="button"
        className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2"
        onClick={() => setShowResult(true)}
        data-testid="fin-submit-btn"
      >
        <TrendingUp size={16} /> Lancer la simulation
      </button>

      {showResult && (
        <div className="mt-5 grid grid-cols-3 gap-2" data-testid="fin-result-panel">
          <Stat label="Mensualite" value={fmtMoney(monthly)} accent />
          <Stat label="Cout total" value={fmtMoney(totalCost)} />
          <Stat label="Budget max" value={fmtMoney(maxBudget)} />
        </div>
      )}

      {showResult && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          <Link
            to={annoncesUrl({ max_price: String(Math.round(maxBudget)) })}
            className="btn-secondary !py-2.5 inline-flex items-center justify-center gap-2 text-sm"
            data-testid="fin-see-compatible-btn"
          >
            Biens compatibles <ArrowRight size={14} />
          </Link>
          <button
            type="button"
            onClick={() => setShowLeadModal(true)}
            className="btn-primary !py-2.5 inline-flex items-center justify-center gap-2 text-sm"
            data-testid="fin-bank-request-btn"
          >
            <Banknote size={14} /> Demande de financement
          </button>
        </div>
      )}

      {showLeadModal && (
        <FinancingLeadModal
          onClose={() => setShowLeadModal(false)}
          presetAmount={Math.round(maxBudget)}
          presetMonthly={Math.round(monthly)}
          presetYears={Number(years)}
        />
      )}
    </article>
  );
}

function FinancingLeadModal({ onClose, presetAmount, presetMonthly, presetYears }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/financing-leads`, {
        name, phone, email, city, note,
        amount: presetAmount, duration_years: presetYears, monthly: presetMonthly,
      });
      toast.success("Demande envoyee — nos partenaires bancaires vous recontactent sous 24h.");
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Echec de l'envoi";
      toast.error(typeof msg === "string" ? msg : "Echec de l'envoi");
    } finally { setLoading(false); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 overflow-y-auto" style={{ zIndex: 9999 }} onClick={onClose} data-testid="lead-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-extrabold tracking-tight">Demande de financement</h3>
            <p className="text-xs text-[var(--ink-soft)] mt-1">
              Vos coordonnees sont partagees avec nos banques partenaires en Cote d&apos;Ivoire.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="lead-modal-close" type="button">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-3 mt-5" data-testid="lead-form">
          <Input label="Nom complet *" value={name} onChange={setName} required testId="lead-name" />
          <Input label="Telephone *" value={phone} onChange={setPhone} required testId="lead-phone" placeholder="+225 0X XX XX XX XX" />
          <Input label="Email" value={email} onChange={setEmail} type="email" testId="lead-email" />
          <Input label="Ville" value={city} onChange={setCity} testId="lead-city" />
          <Textarea label="Note (optionnel)" value={note} onChange={setNote} testId="lead-note" placeholder="Banque preferee, projet, etc." />

          <div className="rounded-2xl bg-orange-50 p-3 text-xs text-orange-800">
            Votre simulation : <strong>{fmtMoney(presetAmount)}</strong> finances sur <strong>{presetYears} ans</strong> · mensualite <strong>{fmtMoney(presetMonthly)}</strong>.
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2" data-testid="lead-submit-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Banknote size={16} />}
            {loading ? "Envoi..." : "Envoyer ma demande"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}

/* ---------- Block 2: Buy ---------- */
function BuyCard() {
  return (
    <article className="glass-card p-7" data-testid="immobilier-tableau-buy">
      <Header icon={Home} label="Acheter une maison / appartement" hue="amber" />
      <p className="text-sm text-[var(--ink-soft)] mt-1">Trouvez le bien qui correspond a votre projet.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
        <SubOption
          to={annoncesUrl({ type_bien: "maison,appartement", statut: "vente", etat_bien: "sans_travaux" })}
          icon={Home}
          title="Sans travaux"
          desc="Pret a habiter, neuf ou renove."
          testId="buy-sans-travaux"
        />
        <SubOption
          to={annoncesUrl({ type_bien: "maison,appartement", statut: "vente", etat_bien: "avec_travaux" })}
          icon={Building2}
          title="Avec travaux"
          desc="A renover ou personnaliser."
          testId="buy-avec-travaux"
        />
        <SubOption
          to={annoncesUrl({ type_bien: "maison,appartement", statut: "vente", etat_bien: "inachevee" })}
          icon={Building2}
          title="Inachevee"
          desc="Construction a terminer."
          testId="buy-inachevee"
        />
      </div>
    </article>
  );
}

/* ---------- Block 3: Rent ---------- */
function RentCard() {
  return (
    <article className="glass-card p-7" data-testid="immobilier-tableau-rent">
      <Header icon={KeySquare} label="Locations maison / appartement" hue="emerald" />
      <p className="text-sm text-[var(--ink-soft)] mt-1">Louez votre futur logement en quelques clics.</p>

      {/* Featured CTA: short-stay furnished rentals (new booking UX) */}
      <Link
        to="/location-meublee"
        className="mt-5 relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-5 flex items-center gap-4 hover:from-emerald-600 hover:to-teal-700 transition-all shadow-sm group"
        data-testid="rent-meublee-featured"
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur grid place-items-center flex-shrink-0">
          <KeySquare size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-100">Nouveau</div>
          <div className="font-bold text-lg leading-tight">Reservation meublee — courte duree</div>
          <div className="text-sm text-emerald-50 mt-0.5">
            Selectionnez le type, choisissez vos dates d&apos;arrivee et de depart. Reservation en 2 min.
          </div>
        </div>
        <ArrowRight size={20} className="flex-shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <SubOption
          to={annoncesUrl({ type_bien: "appartement", statut: "location" })}
          icon={Building2}
          title="Appartements"
          desc="Appartements a louer en zone urbaine."
          testId="rent-apt"
        />
        <SubOption
          to={annoncesUrl({ type_bien: "appartement", statut: "location", subcategory: "Studio" })}
          icon={Home}
          title="Studios"
          desc="Petits espaces, parfaits pour celibataires et etudiants."
          testId="rent-studio"
        />
        <SubOption
          to={annoncesUrl({ type_bien: "maison", statut: "location", subcategory: "Maison meublee" })}
          icon={KeySquare}
          title="Maison meublee"
          desc="Pret-a-vivre, vacances ou court sejour."
          testId="rent-meublee"
        />
        <SubOption
          to={annoncesUrl({ type_bien: "maison", statut: "location", subcategory: "Maison non-meublee" })}
          icon={Home}
          title="Maison non-meublee"
          desc="Location longue duree, vous amenagez."
          testId="rent-non-meublee"
        />
      </div>
    </article>
  );
}

/* ---------- Block 4: Land ---------- */
function LandCard() {
  return (
    <article className="glass-card p-7" data-testid="immobilier-tableau-land">
      <Header icon={Map} label="Terrains — Vente et Location" hue="violet" />
      <p className="text-sm text-[var(--ink-soft)] mt-1">Vendre ou louer un terrain, avec ou sans documents.</p>

      <div className="mt-5">
        <h4 className="text-xs font-bold uppercase text-[var(--ink-soft)] tracking-wide mb-2">Vendre un terrain</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SubOption
            to={annoncesUrl({ type_bien: "terrain", statut: "vente", documents_terrain: "avec_document" })}
            icon={FileCheck2}
            title="Avec documents"
            desc="Titres fonciers ou cadastraux disponibles."
            testId="land-avec-docs"
          />
          <SubOption
            to={annoncesUrl({ type_bien: "terrain", statut: "vente", documents_terrain: "sans_document" })}
            icon={FileX2}
            title="Sans documents"
            desc="Terrains a regulariser, situation a clarifier."
            testId="land-sans-docs"
          />
        </div>
      </div>

      <div className="mt-5">
        <h4 className="text-xs font-bold uppercase text-[var(--ink-soft)] tracking-wide mb-2">Locations de terrain</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SubOption
            to={annoncesUrl({ type_bien: "terrain", statut: "location", usage_terrain: "ville" })}
            icon={Landmark}
            title="Terrain de ville"
            desc="Terrains urbains ou peri-urbains a louer."
            testId="land-rent-ville"
          />
          <SubOption
            to={annoncesUrl({ type_bien: "terrain", statut: "location", usage_terrain: "agricole" })}
            icon={Tractor}
            title="Exploitation agricole"
            desc="Plantations, exploitations, terrains ruraux."
            testId="land-rent-agri"
          />
        </div>
      </div>
    </article>
  );
}

/* ---------- Helpers ---------- */
function Header({ icon: Icon, label, hue }) {
  const hueMap = {
    orange: "from-orange-500 to-orange-400",
    amber: "from-amber-500 to-amber-400",
    emerald: "from-emerald-500 to-emerald-400",
    violet: "from-violet-500 to-violet-400",
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${hueMap[hue] || hueMap.orange} text-white grid place-items-center shadow-lg`}>
        <Icon size={20} />
      </div>
      <h3 className="font-display text-lg font-extrabold tracking-tight">{label}</h3>
    </div>
  );
}

function NumberField({ label, value, onChange, step, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--ink-soft)] block mb-1">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        step={step || "1"}
        min="0"
        className="w-full !py-2.5 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition bg-white"
        data-testid={testId}
      />
    </label>
  );
}

function Input({ label, value, onChange, type, required, testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--ink-soft)] block mb-1">{label}</span>
      <input
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full !py-2.5 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition bg-white"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--ink-soft)] block mb-1">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full !py-2.5 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition bg-white"
      />
    </label>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-3 text-center ${accent ? "bg-orange-500 text-white" : "bg-orange-50 text-orange-700"}`}>
      <div className={`text-[10px] font-bold uppercase ${accent ? "text-orange-100" : "text-orange-700"}`}>{label}</div>
      <div className="font-extrabold text-sm mt-1">{value}</div>
    </div>
  );
}

function SubOption({ to, icon: Icon, title, desc, testId }) {
  return (
    <Link
      to={to}
      className="group block rounded-2xl border-2 border-[var(--line)] p-4 hover:border-orange-400 hover:bg-orange-50 hover:shadow-md transition"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-orange-600" />
        <div className="font-bold text-sm">{title}</div>
      </div>
      <p className="text-xs text-[var(--ink-soft)] mt-1 line-clamp-2">{desc}</p>
      <div
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-600 text-white text-xs font-bold group-hover:bg-orange-700 group-hover:gap-2.5 transition-all"
        data-testid={`${testId}-cta`}
      >
        Voir les annonces <ArrowRight size={13} />
      </div>
    </Link>
  );
}
